import { BillingAddressCheckoutFormDataType } from "@/pages/Order/Checkout/utils";
import { CartType } from "@/stores/cartStore";
import { TokensType } from "@/stores/userStore";
import axios from "axios";

const VITE_WC_STORE_URL = import.meta.env.VITE_WC_STORE_URL;
const VITE_WC_STORE_URL_PATH = import.meta.env.VITE_WC_STORE_URL_PATH;
// Access token and nonce from Zustand

type CartSuccess = {
  cart: CartType;
  cartToken?: string;
  nonce?: string;
};

type CartError = {
  error: true;
  message: string;
};

export type CartResult = CartSuccess | CartError;

export const getCart = async ({
  cartToken,
  nonce,
}: TokensType): Promise<CartResult> => {
  try {
    // Handle the case where there is a cart token and nonce
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (cartToken && nonce) {
      headers["Cart-Token"] = cartToken;
      headers["Nonce"] = nonce;
    }

    const response = await axios.get(
      `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/cart`,
      {
        headers: headers,
      }
    );

    if (!response) {
      throw new Error("Failed to fetch cart");
    }

    const newCartToken = response.headers["cart-token"];
    const newNonce = response.headers["nonce"];

    return { cart: response.data, cartToken: newCartToken, nonce: newNonce };
  } catch (err) {
    console.log(err);
    return {
      error: true,
      message: "Failed to add item to cart",
    };
  }
};

export const addToCart = async ({
  cartToken,
  nonce,
  productId,
  quantity,
  addonsConfiguration,
}: {
  cartToken: string;
  nonce: string;
  productId: number;
  quantity: number;
  addonsConfiguration?: Record<string, any>;
}) => {
  try {
    if (!productId || !quantity) {
      throw new Error("Product ID and quantity are required");
    }

    if (!cartToken || !nonce) {
      throw new Error("No cart token or nonce found");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Cart-Token": cartToken,
      Nonce: nonce,
    };

    const data = {
      id: productId,
      quantity,
      ...(addonsConfiguration && { addons_configuration: addonsConfiguration }),
    };

    const response = await axios({
      method: "POST",
      url: `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/cart/add-item`,
      headers,
      data,
    });

    if (!response || !response.data) {
      throw new Error("Failed to add item to cart");
    }

    return {
      data: response.data,
      cartToken: response.headers["cart-token"],
      nonce: response.headers["nonce"],
    };
  } catch (err: any) {
    const code = err?.response?.data?.code || "unknown_error";
    console.log(err);
    return {
      error: true,
      message: "Failed to add item to cart",
      code,
    };
  }
};

export const updateCartCustomer = async ({
  cartToken,
  nonce,
  customerInfo,
}: {
  cartToken: string;
  nonce: string;
  customerInfo: BillingAddressCheckoutFormDataType;
}) => {
  try {
    if (!cartToken || !nonce) {
      throw new Error("No cart token or nonce found");
    }
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Cart-Token": cartToken,
      Nonce: nonce,
    };

    const response = await axios({
      method: "POST",
      url: `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/cart/update-customer`,
      headers,
      data: {
        ...customerInfo,
      },
    });

    if (!response || !response.data) {
      throw new Error("Failed to update customer in cart");
    }

    return {
      data: response.data,
      cartToken: response.headers["cart-token"],
      nonce: response.headers["nonce"],
    };
  } catch (err: any) {
    console.log(err);

    // Resolve billing address error received from Woocommerce
    const message = err?.response?.data?.message; // "Invalid parameter(s): billing_address"
    const erroredField =
      err?.response?.data?.data?.details?.billing_address?.message; // 'The provided postcode / ZIP is not valid'
    return {
      error: true,
      message: erroredField ?? message ?? "Failed to update customer in cart",
    };
  }
};

export const removeFromCart = async ({
  cartToken,
  nonce,
  cartItemKey,
}: {
  cartToken: string;
  nonce: string;
  cartItemKey: string;
}) => {
  try {
    if (!cartItemKey) {
      throw new Error("Cart item key is required");
    }

    if (!cartToken || !nonce) {
      throw new Error("No cart token or nonce found");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Cart-Token": cartToken,
      Nonce: nonce,
    };

    const response = await axios({
      method: "POST",
      url: `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/cart/remove-item?key=${cartItemKey}`,
      headers,
    });

    if (!response || !response.data) {
      throw new Error("Failed to add item to cart");
    }

    return {
      data: response.data,
      cartToken: response.headers["cart-token"],
      nonce: response.headers["nonce"],
    };
  } catch (err) {
    console.log(err);
    return {
      error: true,
      message: "Failed to add item to cart",
    };
  }
};

export const getProductData = async ({ cartToken, nonce }: TokensType) => {
  try {
    // Handle the case where there is a cart token and nonce
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (cartToken && nonce) {
      headers["Cart-Token"] = cartToken;
      headers["Nonce"] = nonce;
    }

    const productsResponse = await axios.get(
      `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/products?per_page=100`,
      {
        headers: headers,
      }
    );

    if (!productsResponse) {
      throw new Error("Failed to fetch products");
    }
    return productsResponse.data;
  } catch (err) {
    console.log(err);
    return {
      error: true,
      message: "Failed to add item to cart",
    };
  }
};

export const editCartItemQuantity = async ({
  cartToken,
  nonce,
  cartItemKey,
  quantity,
}: {
  cartToken: string;
  nonce: string;
  cartItemKey: string;
  quantity: number;
}) => {
  try {
    if (!cartItemKey) {
      throw new Error("Cart item key is required");
    }

    if (!quantity || quantity < 1) {
      throw new Error("Quantity must be greater than 0");
    }

    if (!cartToken || !nonce) {
      throw new Error("No cart token or nonce found");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Cart-Token": cartToken,
      Nonce: nonce,
    };

    const response = await axios({
      method: "PUT",
      url: `${VITE_WC_STORE_URL}${VITE_WC_STORE_URL_PATH}/cart/items/${cartItemKey}?quantity=${quantity}`,
      headers,
    });

    if (!response || !response.data) {
      throw new Error("Failed to update cart item quantity");
    }

    return {
      data: response.data,
      cartToken: response.headers["cart-token"],
      nonce: response.headers["nonce"],
    };
  } catch (err) {
    console.log(err);
    return {
      error: true,
      message: "Failed to update cart item quantity",
    };
  }
};
